<template>
  <div id="report-aff-detail">

    <!-- ===== SEARCH SECTION ===== -->
    <vx-card class="mb-6">
      <div class="mb-5">
        <h4 class="font-bold text-base">ค้นหาข้อมูลแนะนำเพื่อน</h4>
        <p class="text-sm text-grey mt-1">เลือกช่วงวันที่และ Username AFF เพื่อดูรายงาน</p>
      </div>
      <div class="vx-row">
        <div class="vx-col w-full md:w-1/4 mb-4">
          <label class="block mb-2 text-sm font-semibold">วันที่เริ่มต้น</label>
          <flat-pickr
            :config="configDatePicker"
            v-model="start_date"
            placeholder="เลือกวันที่"
            class="w-full"
          />
        </div>
        <div class="vx-col w-full md:w-1/4 mb-4">
          <label class="block mb-2 text-sm font-semibold">วันที่สิ้นสุด</label>
          <flat-pickr
            :config="configDatePicker"
            v-model="end_date"
            placeholder="เลือกวันที่"
            class="w-full"
          />
        </div>
        <div class="vx-col w-full md:w-1/4 mb-4">
          <label class="block mb-2 text-sm font-semibold">Username AFF</label>
          <vs-input
            v-model="searchAff"
            placeholder="กรอก Username AFF"
            class="w-full"
            @keyup.enter="handleSearch"
          />
        </div>
        <div class="vx-col w-full md:w-1/4 mb-4 flex items-end">
          <vs-button
            color="primary"
            icon="search"
            class="w-full"
            @click="handleSearch"
            :disabled="loading"
          >
            {{ loading ? 'กำลังค้นหา...' : 'ค้นหา' }}
          </vs-button>
        </div>
      </div>
    </vx-card>

    <!-- ===== SUMMARY CARDS ===== -->
    <div class="vx-row" v-if="detailData.length > 0">
      <div class="vx-col w-full sm:w-1/2 md:w-1/4">
        <statistics-card-line
          hideChart
          class="mb-base"
          icon="UsersIcon"
          icon-right
          :statistic="String(detailData.length)"
          statisticTitle="ยูสที่ถูกแนะนำทั้งหมด"
          color="primary"
        />
      </div>
      <div class="vx-col w-full sm:w-1/2 md:w-1/4">
        <statistics-card-line
          hideChart
          class="mb-base"
          icon="CheckCircleIcon"
          icon-right
          :statistic="String(depositedCount)"
          statisticTitle="เติมเงินแล้ว"
          color="success"
        />
      </div>
      <div class="vx-col w-full sm:w-1/2 md:w-1/4">
        <statistics-card-line
          hideChart
          class="mb-base"
          icon="TrendingUpIcon"
          icon-right
          :statistic="totalDeposit | numberWithCommas"
          statisticTitle="ยอดเติมเงินรวม (บาท)"
          color="success"
        />
      </div>
      <div class="vx-col w-full sm:w-1/2 md:w-1/4">
        <statistics-card-line
          hideChart
          class="mb-base"
          icon="TrendingDownIcon"
          icon-right
          :statistic="totalWithdraw | numberWithCommas"
          statisticTitle="ยอดถอนเงินรวม (บาท)"
          color="danger"
        />
      </div>
    </div>

    <!-- ===== DAILY SUMMARY TABLE ===== -->
    <vx-card class="mb-6" v-if="detailData.length > 0">
      <vs-prompt
        title="Export To Excel (รายวัน)"
        class="export-options"
        @cancle="clearDailyFields"
        @accept="exportDailyToExcel"
        accept-text="Export"
        @close="clearDailyFields"
        :active.sync="activePromptDaily"
      >
        <vs-input v-model="fileNameDaily" placeholder="Enter File Name.." class="w-full" />
        <v-select v-model="selectedFormatDaily" :options="formats" class="my-4" />
        <div class="flex">
          <span class="mr-4">Cell Auto Width:</span>
          <vs-switch v-model="cellAutoWidthDaily">Cell Auto Width</vs-switch>
        </div>
      </vs-prompt>

      <div class="mb-4">
        <h4 class="font-bold text-base">ยอดรวมรายวัน</h4>
      </div>
      <vs-table stripe :data="dailySummary">
        <template slot="header">
          <vs-button
            class="my-4"
            :disabled="$store.state.AppActiveUser.permissions.report.action ? false : true"
            @click="activePromptDaily = true"
          >Export</vs-button>
        </template>
        <template slot="thead" class="mt-4">
          <vs-th sort-key="date">วันที่</vs-th>
          <vs-th sort-key="new_members">สมาชิกใหม่</vs-th>
          <vs-th sort-key="deposited">ฝากแล้ว</vs-th>
          <vs-th sort-key="old_customers">ลูกค้าเก่าฝากซ้ำ</vs-th>
          <vs-th sort-key="total_deposit">เติมเงิน (บาท)</vs-th>
          <vs-th sort-key="total_withdraw">ถอนเงิน (บาท)</vs-th>
          <vs-th sort-key="profit">กำไร (บาท)</vs-th>
        </template>
        <template slot-scope="{data}">
          <vs-tr v-for="(tr, i) in data" :key="i">
            <vs-td class="font-semibold">{{ tr.date | formatDate }}</vs-td>
            <vs-td>{{ tr.new_members }}</vs-td>
            <vs-td class="text-success font-semibold">{{ tr.deposited }}</vs-td>
            <vs-td class="text-primary font-semibold">{{ tr.old_customers || 0 }}</vs-td>
            <vs-td class="text-success font-semibold">{{ tr.total_deposit | numberWithCommas }}</vs-td>
            <vs-td class="text-danger font-semibold">{{ tr.total_withdraw | numberWithCommas }}</vs-td>
            <vs-td :class="tr.profit >= 0 ? 'text-success font-semibold' : 'text-danger font-semibold'">
              {{ tr.profit | numberWithCommas }}
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </vx-card>

    <!-- ===== DETAIL TABLE ===== -->
    <vx-card v-if="detailData.length > 0">
      <vs-prompt
        title="Export To Excel"
        class="export-options"
        @cancle="clearFields"
        @accept="exportToExcel"
        accept-text="Export"
        @close="clearFields"
        :active.sync="activePrompt"
      >
        <vs-input v-model="fileName" placeholder="Enter File Name.." class="w-full" />
        <v-select v-model="selectedFormat" :options="formats" class="my-4" />
        <div class="flex">
          <span class="mr-4">Cell Auto Width:</span>
          <vs-switch v-model="cellAutoWidth">Cell Auto Width</vs-switch>
        </div>
      </vs-prompt>

      <vs-table stripe pagination max-items="20" :data="detailData" search>
        <template slot="header">
          <vs-button
            :disabled="$store.state.AppActiveUser.permissions.report.action ? false : true"
            @click="activePrompt = true"
          >Export</vs-button>
        </template>

        <template slot="thead">
          <vs-th sort-key="member_username">Username</vs-th>
          <vs-th sort-key="member_level">สถานะ</vs-th>
          <vs-th sort-key="total_deposit">เติมเงิน (บาท)</vs-th>
          <vs-th sort-key="total_withdraw">ถอนเงิน (บาท)</vs-th>
          <vs-th>กำไร (บาท)</vs-th>
          <vs-th sort-key="latest_topup_datetime">เติมเงินล่าสุด</vs-th>
          <vs-th sort-key="member_register_date">วันที่สมัคร</vs-th>
        </template>

        <template slot-scope="{data}">
          <vs-tr v-for="(tr, i) in data" :key="i">
            <vs-td class="text-danger font-semibold">
              <router-link :to="`/member/${tr.member_username}`">{{ tr.member_username }}</router-link>
            </vs-td>
            <vs-td>
              <vs-chip :color="tr.member_level === 0 ? 'danger' : 'success'">
                {{ tr.member_level === 0 ? 'ยังไม่เติมเงิน' : 'เติมเงินแล้ว' }}
              </vs-chip>
            </vs-td>
            <vs-td class="text-success font-semibold">
              {{ (tr.total_deposit || 0) | numberWithCommas }}
            </vs-td>
            <vs-td class="text-danger font-semibold">
              {{ (tr.total_withdraw || 0) | numberWithCommas }}
            </vs-td>
            <vs-td :class="(tr.total_deposit - tr.total_withdraw) >= 0 ? 'text-success font-semibold' : 'text-danger font-semibold'">
              {{ ((tr.total_deposit || 0) - (tr.total_withdraw || 0)) | numberWithCommas }}
            </vs-td>
            <vs-td>
              <div>{{ tr.latest_topup_datetime | formatDateTime }}</div>
              <small
                v-if="tr.latest_topup_datetime"
                :class="getTimeAgoClass(tr.latest_topup_datetime)"
              >{{ tr.latest_topup_datetime | timeAgo }}</small>
            </vs-td>
            <vs-td>{{ tr.member_register_date | formatDate }}</vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </vx-card>

  </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import vSelect from 'vue-select'
import StatisticsCardLine from '@/components/statistics-cards/StatisticsCardLine.vue'
import axios from '../../axios'
import moment from 'moment'

export default {
  components: {
    flatPickr,
    vSelect,
    StatisticsCardLine
  },

  filters: {
    numberWithCommas (val) {
      if (!val && val !== 0) return '-'
      return Number(val).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatDate (val) {
      if (!val) return '-'
      return moment(val).format('DD/MM/YYYY')
    },
    formatDateTime (val) {
      if (!val) return '-'
      return moment(val).format('DD/MM/YYYY HH:mm:ss')
    },
    timeAgo (val) {
      if (!val) return ''
      const now = moment()
      const target = moment(val)
      const hoursDiff = now.diff(target, 'hours', true)
      if (hoursDiff < 24) {
        const hours = Math.max(1, Math.ceil(hoursDiff))
        return `${hours} ชม. ที่ผ่านมา`
      }
      const daysDiff = now.diff(target, 'days')
      return `${daysDiff} วันที่ผ่านมา`
    }
  },

  data () {
    return {
      start_date: moment().format('YYYY-MM-DD'),
      end_date: moment().add(1, 'days').format('YYYY-MM-DD'),
      searchAff: '',
      configDatePicker: {
        enableTime: false,
        dateFormat: 'Y-m-d'
      },

      loading: false,
      detailData: [],
      dailySummaryData: [],

      // Export - detail
      activePrompt: false,
      fileName: '',
      formats: ['xlsx', 'csv', 'txt'],
      selectedFormat: 'xlsx',
      cellAutoWidth: true,
      headerTitle: ['AFF (ผู้แนะนำ)', 'Username', 'สถานะ', 'เติมเงิน (บาท)', 'ถอนเงิน (บาท)', 'กำไร (บาท)', 'เติมเงินล่าสุด', 'วันที่สมัคร'],
      headerVal: ['member_aff', 'member_username', 'member_level_text', 'total_deposit', 'total_withdraw', 'profit', 'latest_topup_datetime', 'member_register_date'],
      // Export - daily
      activePromptDaily: false,
      fileNameDaily: '',
      selectedFormatDaily: 'xlsx',
      cellAutoWidthDaily: true
    }
  },

  computed: {
    totalDeposit () {
      return this.detailData.reduce((sum, r) => sum + (parseFloat(r.total_deposit) || 0), 0)
    },
    totalWithdraw () {
      return this.detailData.reduce((sum, r) => sum + (parseFloat(r.total_withdraw) || 0), 0)
    },
    depositedCount () {
      return this.detailData.filter(r => r.member_level === 1).length
    },
    dailySummary () {
      return this.dailySummaryData
    }
  },

  methods: {
    getTimeAgoClass (val) {
      if (!val) return ''
      const hoursDiff = moment().diff(moment(val), 'hours', true)
      if (hoursDiff < 24) return 'text-success font-semibold'
      if (hoursDiff < 24 * 7) return 'text-warning font-semibold'
      return 'text-danger font-semibold'
    },

    async handleSearch () {
      if (!this.start_date || !this.end_date) {
        this.$vs.notify({ title: 'แจ้งเตือน', text: 'กรุณาเลือกวันที่ก่อน', color: 'warning' })
        return
      }
      if (!this.searchAff.trim()) {
        this.$vs.notify({ title: 'แจ้งเตือน', text: 'กรุณากรอก Username AFF ก่อนค้นหา', color: 'warning' })
        return
      }
      this.loading = true
      this.detailData = []
      this.dailySummaryData = []
      try {
        const aff = this.searchAff.trim()
        const [detailRes, dailyRes] = await Promise.all([
          axios.get(`reportaff/affmemberdetail/${aff}/${this.start_date}/${this.end_date}`),
          axios.get(`reportaff/affdailysummary/${aff}/${this.start_date}/${this.end_date}`)
        ])
        this.detailData = detailRes.data || []
        this.dailySummaryData = dailyRes.data || []
        this.fileName = `Report_AffDetail_${this.start_date}_${this.end_date}`
        this.fileNameDaily = `Report_AffDaily_${this.start_date}_${this.end_date}`
        if (this.detailData.length === 0) {
          this.$vs.notify({ title: 'ไม่พบข้อมูล', text: 'ไม่มีข้อมูลในช่วงวันที่ที่เลือก', color: 'warning' })
        }
      } catch (e) {
        this.$vs.notify({ title: 'เกิดข้อผิดพลาด', text: 'ไม่สามารถโหลดข้อมูลได้', color: 'danger' })
      } finally {
        this.loading = false
      }
    },

    exportToExcel () {
      import('@/vendor/Export2Excel').then(excel => {
        const exportData = this.detailData.map(r => ({
          ...r,
          member_level_text: r.member_level === 0 ? 'ยังไม่เติมเงิน' : 'เติมเงินแล้ว',
          profit: (parseFloat(r.total_deposit) || 0) - (parseFloat(r.total_withdraw) || 0),
          latest_topup_datetime: r.latest_topup_datetime ? moment(r.latest_topup_datetime).format('DD/MM/YYYY HH:mm:ss') : '-'
        }))
        const data = this.formatJson(this.headerVal, exportData)
        excel.export_json_to_excel({
          header: this.headerTitle,
          data,
          filename: this.fileName,
          autoWidth: this.cellAutoWidth,
          bookType: this.selectedFormat
        })
        this.clearFields()
      })
    },

    exportDailyToExcel () {
      import('@/vendor/Export2Excel').then(excel => {
        const data = this.formatJson(['date', 'new_members', 'deposited', 'old_customers', 'total_deposit', 'total_withdraw', 'profit'], this.dailySummary)
        excel.export_json_to_excel({
          header: ['วันที่', 'สมาชิกใหม่', 'ฝากแล้ว', 'ลูกค้าเก่าฝากซ้ำ', 'เติมเงิน (บาท)', 'ถอนเงิน (บาท)', 'กำไร (บาท)'],
          data,
          filename: this.fileNameDaily || `Report_AffDaily_${this.start_date}_${this.end_date}`,
          autoWidth: this.cellAutoWidthDaily,
          bookType: this.selectedFormatDaily
        })
        this.clearDailyFields()
      })
    },

    formatJson (filterVal, jsonData) {
      return jsonData.map(v => filterVal.map(j => v[j]))
    },

    clearFields () {
      this.fileName = ''
      this.cellAutoWidth = true
      this.selectedFormat = 'xlsx'
    },

    clearDailyFields () {
      this.fileNameDaily = ''
      this.cellAutoWidthDaily = true
      this.selectedFormatDaily = 'xlsx'
    }
  }
}
</script>
